/**
 * Simple logging wrapper for hobby app
 * Replaces the 191-line enterprise logging system
 */

// Simple log levels
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

// Basic log entry interface for compatibility
export interface LogEntry {
  timestamp: string
  level: LogLevel
  message: string
  data?: unknown
  error?: Error
  context?: string
}

// Simple console wrapper
class SimpleLogger {
  private isDevelopment = process.env.NODE_ENV === 'development'

  info(message: string, data?: unknown, context?: string): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(`[INFO]${context ? ` [${context}]` : ''} ${message}`, data || '')
    }
  }

  error(message: string, error?: Error, data?: unknown, context?: string): void {
    // eslint-disable-next-line no-console
    console.error(`[ERROR]${context ? ` [${context}]` : ''} ${message}`, error || '', data || '')
  }

  warn(message: string, data?: unknown, context?: string): void {
    // eslint-disable-next-line no-console
    console.warn(`[WARN]${context ? ` [${context}]` : ''} ${message}`, data || '')
  }

  debug(message: string, data?: unknown, context?: string): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.debug(`[DEBUG]${context ? ` [${context}]` : ''} ${message}`, data || '')
    }
  }

  // Compatibility method for timing operations
  time(label: string): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.time(label)
    }
  }

  timeEnd(label: string): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.timeEnd(label)
    }
  }

  // Legacy compatibility methods
  writeLog(entry: LogEntry): Promise<boolean> {
    switch (entry.level) {
      case LogLevel.DEBUG:
        this.debug(entry.message, entry.data, entry.context)
        break
      case LogLevel.INFO:
        this.info(entry.message, entry.data, entry.context)
        break
      case LogLevel.WARN:
        this.warn(entry.message, entry.data, entry.context)
        break
      case LogLevel.ERROR:
        this.error(entry.message, entry.error, entry.data, entry.context)
        break
    }
    return Promise.resolve(true)
  }
}

// Export singleton instance
export const log = new SimpleLogger()

// Export class for compatibility
export { SimpleLogger as Logger }

// Default export for backward compatibility
export default log
